import FormInput from '../form-input'
import React, { ReactElement } from 'react'
import { Chip, Grid, Stack, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'

/**
 * One throttling field. The input holds the value that *will* be in force; when
 * a change is pending the annotation underneath shows what the store is running
 * on today, so the row never implies the new number is already live.
 */
const OrderThrottlingRow = ({
  canEdit,
  error,
  helperText,
  inForce,
  label,
  max,
  min,
  onCancelPending,
  onChange,
  pending,
  pendingDay,
  readOnly,
  readOnlyNote,
  value,
}: Props): ReactElement => {
  const { classes } = useStyles()

  const renderPending = () => {
    if (pending == null) return null

    return (
      <Stack
        alignItems='center'
        className={classes.pending}
        direction='row'
        spacing={0.5}
      >
        <Typography className={classes.inForce} variant='body2'>
          {inForce}
        </Typography>
        <Typography color='text.secondary' variant='body2'>
          &rarr;
        </Typography>
        <Typography variant='body2'>{pending}</Typography>
        <Chip
          color='warning'
          label={pendingDay ? `Pending · ${pendingDay}` : 'Pending'}
          onDelete={canEdit ? onCancelPending : undefined}
          size='small'
          variant='outlined'
        />
      </Stack>
    )
  }

  if (readOnly) {
    return (
      <Grid item lg={4} xs={6}>
        <Typography color='text.secondary' variant='caption'>
          {label}
        </Typography>
        <Typography variant='body1'>{value}</Typography>
        {renderPending()}
        {readOnlyNote && (
          <Typography color='text.secondary' variant='caption'>
            {readOnlyNote}
          </Typography>
        )}
      </Grid>
    )
  }

  return (
    <Grid item lg={4} xs={6}>
      <FormInput
        disabled={!canEdit}
        error={!!error}
        helperText={error || helperText}
        inputProps={{ type: 'number', min, max, step: 1 }}
        onChangeText={onChange}
        title={label}
        value={value}
      />
      {renderPending()}
    </Grid>
  )
}

export default OrderThrottlingRow

type Props = {
  canEdit: boolean
  error?: string
  helperText?: string
  inForce?: number
  label: string
  max?: number
  min?: number
  /** Omitted for a read-only field — cancelling one means writing it. */
  onCancelPending?: () => void
  onChange: (text: string) => void
  pending?: number
  pendingDay?: string
  readOnly?: boolean
  readOnlyNote?: string
  value: string
}

const useStyles = makeStyles()((theme) => ({
  pending: {
    marginTop: theme.spacing(0.5),
  },
  inForce: {
    textDecoration: 'line-through',
    color: theme.palette.text.secondary,
  },
}))
